"use client";

import { NavArrowDown, Search } from "iconoir-react";
import { debounce, parseAsArrayOf, parseAsString, useQueryStates } from "nuqs";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  filterPartners,
  getFacetOptions,
  hasActiveFilters,
  sanitizePartnerFilters,
  toggleFilterValue,
} from "@/lib/partner-directory";
import type { PartnerFilterKey } from "@/lib/partner-directory";
import { cn } from "@/lib/utils";

import { DirectoryGrid, DirectoryShell } from "./directory-shell";
import { MultiSelectFilter } from "./multi-select-filter";
import type { PartnerDirectoryCardData, PartnerDirectoryLabels } from "./types";

export interface PartnerDirectoryClientProps {
  partners: PartnerDirectoryCardData[];
  labels: PartnerDirectoryLabels;
  pageSize: number;
}

const FILTER_FIELDS: {
  key: PartnerFilterKey;
  label: keyof PartnerDirectoryLabels;
}[] = [
  { key: "stages", label: "stages" },
  { key: "industries", label: "industries" },
  { key: "countries", label: "countries" },
  { key: "categories", label: "categories" },
];

const listParser = parseAsArrayOf(parseAsString).withDefault([]);

/** URL state: `?q=…&countries=Spain&countries=Norway`. Shallow, so the static page is not refetched. */
const filterParsers = {
  q: parseAsString.withDefault(""),
  stages: listParser,
  industries: listParser,
  countries: listParser,
  categories: listParser,
};

const QUERY_URL_DEBOUNCE = debounce(250);

export function PartnerDirectoryClient({
  partners,
  labels,
  pageSize,
}: PartnerDirectoryClientProps) {
  const facetOptions = useMemo(() => getFacetOptions(partners), [partners]);
  const [urlState, setUrlState] = useQueryStates(filterParsers, {
    history: "replace",
  });
  const filters = useMemo(
    () => sanitizePartnerFilters(urlState, facetOptions),
    [urlState, facetOptions]
  );
  // Reveal state is keyed by the active filters so changing a filter starts
  // again from the first page without an effect.
  const filterKey = JSON.stringify(filters);
  const [reveal, setReveal] = useState({ key: filterKey, count: pageSize });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const focusIndexRef = useRef<number | null>(null);

  const matches = useMemo(
    () => filterPartners(partners, filters),
    [partners, filters]
  );
  const visibleCount = reveal.key === filterKey ? reveal.count : pageSize;
  const visible = matches.slice(0, visibleCount);
  const hasMore = matches.length > visible.length;
  const active = hasActiveFilters(filters);

  // Move focus to the first newly revealed card so keyboard users keep their place.
  useEffect(() => {
    const index = focusIndexRef.current;
    if (index === null) return;
    focusIndexRef.current = null;
    listRef.current?.querySelectorAll<HTMLAnchorElement>("a")[index]?.focus();
  }, [visible.length]);

  function toggleFilter(
    key: PartnerFilterKey,
    value: string,
    checked: boolean
  ) {
    const next = toggleFilterValue(filters, key, value, checked)[key];
    void setUrlState({ [key]: next.length > 0 ? next : null });
  }

  function clearFilters() {
    void setUrlState(null);
  }

  function loadMore() {
    focusIndexRef.current = visible.length;
    setReveal({ key: filterKey, count: visibleCount + pageSize });
  }

  const resultCount = (
    matches.length === 1 ? labels.resultCountOne : labels.resultCountOther
  ).replace("{count}", String(matches.length));

  return (
    <DirectoryShell
      sidebar={
        <div className="bg-grey-0 flex flex-col">
          <div className="border-grey-100 flex h-[57px] items-center border-b lg:h-[49px]">
            <h2 className="text-ds-body-xs text-grey-950 hidden px-4 lg:block">
              {labels.heading}
            </h2>
            <button
              type="button"
              aria-expanded={filtersOpen}
              aria-controls={filtersId}
              onClick={() => setFiltersOpen((open) => !open)}
              className="text-ds-body-xs text-grey-950 focus-visible:ring-pulse-400 flex h-full w-full items-center justify-between px-5 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset lg:hidden"
            >
              {labels.toggle}
              <NavArrowDown
                aria-hidden
                className={cn(
                  "size-4 transition-transform duration-200 motion-reduce:transition-none",
                  filtersOpen && "rotate-180"
                )}
              />
            </button>
          </div>
          <div
            id={filtersId}
            className={cn(
              "flex-col gap-3 p-4",
              filtersOpen ? "flex" : "hidden lg:flex"
            )}
          >
            <div className="relative">
              <Search
                aria-hidden
                className="text-grey-600 pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2"
              />
              <input
                type="search"
                value={filters.query}
                aria-label={labels.searchLabel}
                placeholder={labels.searchPlaceholder}
                onChange={(event) =>
                  void setUrlState(
                    { q: event.target.value || null },
                    { limitUrlUpdates: QUERY_URL_DEBOUNCE }
                  )
                }
                className="border-grey-100 bg-grey-0 text-ds-body-xs text-grey-950 placeholder:text-grey-600 rounded-ds-m focus-visible:ring-pulse-400 h-10 w-full border pr-4 pl-10 focus-visible:ring-2 focus-visible:outline-none"
              />
            </div>
            {FILTER_FIELDS.map(({ key, label }) => (
              <MultiSelectFilter
                key={key}
                label={labels[label]}
                options={facetOptions[key]}
                selected={filters[key]}
                placeholder={labels.selectPlaceholder}
                selectedCountLabel={labels.selectedCount}
                onToggle={(value, checked) => toggleFilter(key, value, checked)}
              />
            ))}
            {active ? (
              <button
                type="button"
                onClick={clearFilters}
                className="text-ds-body-xs text-pulse-700 focus-visible:ring-pulse-400 w-fit underline underline-offset-4 focus-visible:ring-2 focus-visible:outline-none"
              >
                {labels.clear}
              </button>
            ) : null}
          </div>
        </div>
      }
      footer={
        hasMore ? (
          <div className="flex justify-center pt-10">
            <Button variant="secondary" onClick={loadMore}>
              {labels.loadMore}
            </Button>
          </div>
        ) : null
      }
    >
      <p role="status" className="sr-only">
        {resultCount}
      </p>
      {visible.length > 0 ? (
        <DirectoryGrid
          ref={listRef}
          partners={visible}
          label={labels.listLabel}
        />
      ) : (
        <div className="flex flex-col items-start gap-2 py-8">
          <h3 className="text-ds-heading-h4 text-grey-950">
            {labels.emptyTitle}
          </h3>
          <p className="text-ds-body-md text-grey-600">
            {labels.emptyDescription}
          </p>
        </div>
      )}
    </DirectoryShell>
  );
}
